import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FRONT_DESK_ROLE_ID, HOUSEKEEPING_ROLE_ID } from '@/lib/roles';

interface Department { id: string; department_name: string }

interface Props {
  departments: Department[];
  todayDepartmentName?: string | null;
  roleId?: string;
  isManager: boolean;
}

interface Action {
  label: string;
  note: string;
  icon: string;
  to: string;
  external?: boolean;
}

const departmentTheme: Record<string, { icon: string; color: string; eyebrow: string }> = {
  'Casa Kokoro': { icon: 'ri-hotel-line', color: '#234638', eyebrow: 'Stay Operations' },
  "Goldy’s": { icon: 'ri-restaurant-line', color: '#9A6A2F', eyebrow: 'Restaurant Operations' },
  'HanaMomo Express': { icon: 'ri-taxi-line', color: '#315C79', eyebrow: 'Transport Operations' },
  'HanaMomo Car Rental': { icon: 'ri-car-line', color: '#6C5634', eyebrow: 'Rental Operations' },
  'HanaMomo Car Sales': { icon: 'ri-roadster-line', color: '#6A3F3B', eyebrow: 'Sales Operations' },
  'HanaMomo SIM': { icon: 'ri-sim-card-line', color: '#4B597C', eyebrow: 'SIM Operations' },
};

const externalUrls: Record<string, string> = {
  'HanaMomo Express': 'https://hanamomoexpress.com/admin',
  'HanaMomo Car Rental': 'https://hanamomocar.com/admin',
  'HanaMomo Car Sales': 'https://hanamomocars.com/admin',
  'HanaMomo SIM': 'https://hanamomosim.com/admin',
};

function actionsFor(name: string, roleId: string | undefined, isManager: boolean): Action[] {
  if (name === 'Casa Kokoro') {
    const operationPath = roleId === HOUSEKEEPING_ROLE_ID ? '/cleaning' : roleId === FRONT_DESK_ROLE_ID ? '/reservations' : '/casa';
    return [
      { label: roleId === HOUSEKEEPING_ROLE_ID ? '担当客室・清掃' : '本日のIN・OUT', note: roleId === HOUSEKEEPING_ROLE_ID ? '清掃状況と引継ぎを確認' : '到着・出発と客室状況', icon: roleId === HOUSEKEEPING_ROLE_ID ? 'ri-brush-3-line' : 'ri-door-open-line', to: operationPath },
      { label: '予約・注意事項', note: 'ゲスト情報と申し送り', icon: 'ri-file-list-3-line', to: roleId === HOUSEKEEPING_ROLE_ID ? '/cleaning' : '/reservations' },
      { label: '発注・手配', note: '不足品や必要日を登録', icon: 'ri-shopping-bag-3-line', to: '/purchase-requests' },
      ...(isManager ? [{ label: '部門の勤怠承認', note: '未承認・差異を確認', icon: 'ri-user-follow-line', to: '/attendance/admin' }] : []),
    ];
  }
  if (name === "Goldy’s") return [
    { label: 'Goldy’s 店舗業務', note: '営業・支払・発注をまとめて確認', icon: 'ri-store-2-line', to: '/goldys' },
    { label: '日次締め・現金', note: '売上・在高・レシート', icon: 'ri-cash-line', to: '/goldys-closing' },
    { label: '発注・手配', note: '必要品と納期を登録', icon: 'ri-shopping-bag-3-line', to: '/purchase-requests' },
    ...(isManager ? [{ label: '部門の勤怠承認', note: '未承認・差異を確認', icon: 'ri-user-follow-line', to: '/attendance/admin' }] : []),
  ];

  const labels: Record<string, [string, string, string]> = {
    'HanaMomo Express': ['本日の送迎', '迎車時刻・乗車場所・車両', 'ri-taxi-line'],
    'HanaMomo Car Rental': ['本日の貸出・返却', '担当車両と準備状況', 'ri-key-2-line'],
    'HanaMomo Car Sales': ['商談・フォロー', '来店予定と対応期限', 'ri-user-star-line'],
    'HanaMomo SIM': ['申込・開通対応', '発送・開通待ちを確認', 'ri-sim-card-line'],
  };
  const [label, note, icon] = labels[name] || ['部門業務', '担当内容を確認', 'ri-briefcase-line'];
  const url = externalUrls[name] || '/';
  return [
    { label, note, icon, to: url, external: true },
    { label: '発注・手配', note: '必要品と納期を登録', icon: 'ri-shopping-bag-3-line', to: '/purchase-requests' },
    { label: '部門メッセージ', note: '連絡と引継ぎを確認', icon: 'ri-chat-3-line', to: '/messages' },
    ...(isManager ? [{ label: '部門の勤怠承認', note: '未承認・差異を確認', icon: 'ri-user-follow-line', to: '/attendance/admin' }] : []),
  ];
}

export default function DepartmentWorkspace({ departments, todayDepartmentName, roleId, isManager }: Props) {
  const initial = departments.find((d) => d.department_name === todayDepartmentName)?.id || departments[0]?.id || '';
  const [activeId, setActiveId] = useState(initial);

  useEffect(() => {
    const next = departments.find((d) => d.department_name === todayDepartmentName)?.id || departments[0]?.id || '';
    setActiveId(next);
  }, [departments, todayDepartmentName]);

  const active = departments.find((d) => d.id === activeId) || departments[0];
  const actions = useMemo(() => active ? actionsFor(active.department_name, roleId, isManager) : [], [active, roleId, isManager]);

  if (!active) return <section className="rounded-xl border border-dashed border-background-300 bg-background-50 p-5">
    <h3 className="font-bold text-foreground-900">所属部門を設定してください</h3>
    <p className="mt-1 text-sm text-foreground-600">管理者が所属部門を登録すると、ここに今日の業務が表示されます。</p>
  </section>;

  const theme = departmentTheme[active.department_name] || { icon: 'ri-building-line', color: '#234638', eyebrow: 'Department Operations' };
  return <section className="overflow-hidden rounded-2xl border border-background-200 bg-background-50">
    <div className="border-b border-background-200 px-4 py-4 md:px-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl text-white" style={{ backgroundColor: theme.color }}><i className={`${theme.icon} text-xl`} /></span>
          <div><p className="text-[10px] font-bold uppercase tracking-[.16em] text-foreground-500">{theme.eyebrow}</p><h3 className="text-lg font-extrabold text-foreground-900">{active.department_name}・今日の業務</h3></div>
        </div>
        {departments.length > 1 && <div className="flex max-w-full gap-1 overflow-x-auto rounded-xl bg-background-100 p-1">
          {departments.map((department) => <button key={department.id} onClick={() => setActiveId(department.id)} className={`whitespace-nowrap rounded-lg px-3 py-2 text-xs font-bold transition ${active.id === department.id ? 'bg-white text-primary-800 shadow-sm' : 'text-foreground-600'}`}>{department.department_name}</button>)}
        </div>}
      </div>
      {todayDepartmentName === active.department_name && <p className="mt-3 inline-flex items-center rounded-full bg-primary-50 px-2.5 py-1 text-[11px] font-bold text-primary-700"><i className="ri-calendar-check-line mr-1" />本日のシフト先</p>}
    </div>
    <div className="grid grid-cols-1 gap-3 p-3 sm:grid-cols-2 lg:grid-cols-4">
      {actions.map((action) => {
        const content = <><span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white text-primary-700 shadow-sm"><i className={`${action.icon} text-base`} /></span><span className="min-w-0"><strong className="block text-sm text-foreground-900">{action.label}</strong><span className="mt-0.5 block text-[11px] leading-snug text-foreground-500">{action.note}</span></span><i className={`${action.external ? 'ri-arrow-right-up-line' : 'ri-arrow-right-s-line'} ml-auto text-foreground-400`} /></>;
        const className = 'flex min-h-[82px] items-center gap-3 rounded-xl border border-background-200 bg-white p-3 transition hover:border-primary-300 hover:shadow-sm';
        return action.external ? <a key={action.label} href={action.to} target="_blank" rel="noreferrer" className={className}>{content}</a> : <Link key={action.label} to={action.to} className={className}>{content}</Link>;
      })}
    </div>
    <div className="grid grid-cols-2 border-t border-background-200 bg-white md:grid-cols-4">
      <Link to="/shifts" className="border-r border-background-200 px-4 py-3 text-center text-xs font-bold text-foreground-700 hover:bg-background-50"><i className="ri-calendar-schedule-line mr-1 text-primary-700" />チームシフト</Link>
      <Link to="/messages" className="border-r border-background-200 px-4 py-3 text-center text-xs font-bold text-foreground-700 hover:bg-background-50"><i className="ri-chat-3-line mr-1 text-primary-700" />メッセージ</Link>
      <Link to="/notifications" className="border-r border-background-200 px-4 py-3 text-center text-xs font-bold text-foreground-700 hover:bg-background-50"><i className="ri-notification-3-line mr-1 text-primary-700" />通知・承認</Link>
      <Link to="/reimbursements" className="px-4 py-3 text-center text-xs font-bold text-foreground-700 hover:bg-background-50"><i className="ri-receipt-line mr-1 text-primary-700" />立替・レシート</Link>
    </div>
  </section>;
}
